import React, { useState, useEffect, useCallback } from "react";
import { API, useLanguage } from "../App";
import { Button } from "./ui/button";
import {
  MessageSquare, Check, Loader2, AlertTriangle, Wifi, WifiOff,
  Phone, Settings2, X, RefreshCw, Unplug
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const WhatsAppConnect = () => {
  const { language } = useLanguage();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [appId, setAppId] = useState(null);
  const [sdkReady, setSdkReady] = useState(false);

  const t = (it, en) => language === "it" ? it : en;

  // Fetch status and app ID on mount
  useEffect(() => {
    const load = async () => {
      try {
        const [statusRes, appRes] = await Promise.all([
          axios.get(`${API}/whatsapp/status`, { withCredentials: true }),
          axios.get(`${API}/whatsapp/app-id`),
        ]);
        setStatus(statusRes.data);
        setAppId(appRes.data.app_id);
      } catch { }
      finally { setLoading(false); }
    };
    load();
  }, []);

  // Load Facebook SDK
  useEffect(() => {
    if (!appId) return;
    if (window.FB) { setSdkReady(true); return; }

    window.fbAsyncInit = function () {
      window.FB.init({ appId, cookie: true, xfbml: true, version: "v21.0" });
      setSdkReady(true);
    };

    if (!document.getElementById("facebook-jssdk")) {
      const script = document.createElement("script");
      script.id = "facebook-jssdk";
      script.src = "https://connect.facebook.net/en_US/sdk.js";
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    }
  }, [appId]);

  const startEmbeddedSignup = useCallback(() => {
    if (!window.FB || !sdkReady) {
      toast.error(t("SDK Facebook non caricato. Riprova.", "Facebook SDK not loaded. Try again."));
      return;
    }

    setConnecting(true);

    window.FB.login(
      function (response) {
        if (response.authResponse) {
          const code = response.authResponse.code;
          exchangeCode(code);
        } else {
          setConnecting(false);
          toast.error(t("Accesso annullato", "Login cancelled"));
        }
      },
      {
        config_id: undefined, // Use default config
        response_type: "code",
        override_default_response_type: true,
        extras: {
          feature: "whatsapp_embedded_signup",
          sessionInfoVersion: 2,
        },
      }
    );
  }, [sdkReady]);

  const exchangeCode = async (code) => {
    try {
      const res = await axios.post(`${API}/whatsapp/embedded-signup`, { code }, { withCredentials: true });
      if (res.data.success) {
        toast.success(t(
          `WhatsApp collegato! Numero: ${res.data.phone_number}`,
          `WhatsApp connected! Number: ${res.data.phone_number}`
        ));
        // Refresh status
        const statusRes = await axios.get(`${API}/whatsapp/status`, { withCredentials: true });
        setStatus(statusRes.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t("Errore nella connessione", "Connection error"));
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm(t("Sei sicuro di voler disconnettere WhatsApp?", "Are you sure you want to disconnect WhatsApp?"))) return;
    setDisconnecting(true);
    try {
      await axios.post(`${API}/whatsapp/disconnect`, {}, { withCredentials: true });
      toast.success(t("WhatsApp disconnesso", "WhatsApp disconnected"));
      setStatus({ connected: false, status: "not_connected" });
    } catch { toast.error("Errore"); }
    finally { setDisconnecting(false); }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border bg-card p-6">
        <div className="flex items-center gap-3"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /><span className="text-sm text-muted-foreground">{t("Caricamento...", "Loading...")}</span></div>
      </div>
    );
  }

  const connected = status?.connected;

  return (
    <div className="rounded-2xl border bg-card overflow-hidden" data-testid="whatsapp-connect">
      {/* Header */}
      <div className={`px-6 py-4 flex items-center gap-3 ${connected ? "bg-emerald-50 dark:bg-emerald-950/20 border-b border-emerald-200 dark:border-emerald-800" : "bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700"}`}>
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${connected ? "bg-emerald-100 dark:bg-emerald-900/30" : "bg-slate-200 dark:bg-slate-700"}`}>
          <MessageSquare className={`w-5 h-5 ${connected ? "text-emerald-600 dark:text-emerald-400" : "text-slate-500"}`} />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-foreground text-sm">{t("WhatsApp Business", "WhatsApp Business")}</h3>
          <p className="text-xs text-muted-foreground">
            {connected ? t("Connesso e attivo", "Connected and active") : t("Non collegato", "Not connected")}
          </p>
        </div>
        <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${connected ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" : "bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-400"}`}>
          {connected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
          {connected ? t("Attivo", "Active") : t("Inattivo", "Inactive")}
        </div>
      </div>

      <div className="p-6">
        {connected ? (
          /* Connected state */
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded-xl bg-muted/50">
                <p className="text-xs text-muted-foreground">{t("Numero", "Number")}</p>
                <p className="font-semibold text-sm text-foreground flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-emerald-500" />{status.phone_number || "N/A"}</p>
              </div>
              <div className="p-3 rounded-xl bg-muted/50">
                <p className="text-xs text-muted-foreground">{t("Attività", "Business")}</p>
                <p className="font-semibold text-sm text-foreground">{status.business_name || "N/A"}</p>
              </div>
            </div>
            {status.connected_at && (
              <p className="text-xs text-muted-foreground">{t("Connesso dal", "Connected since")} {new Date(status.connected_at).toLocaleDateString("it-IT")}</p>
            )}
            <Button variant="outline" size="sm" onClick={handleDisconnect} disabled={disconnecting} className="rounded-full text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/10" data-testid="wa-disconnect-btn">
              {disconnecting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Unplug className="w-4 h-4 mr-1.5" />}
              {t("Disconnetti", "Disconnect")}
            </Button>
          </div>
        ) : (
          /* Not connected state */
          <div className="space-y-5">
            <div className="space-y-3">
              {[
                t("Risposte automatiche ai clienti 24/7", "Automatic client responses 24/7"),
                t("Prenotazione conversazionale via WhatsApp", "Conversational booking via WhatsApp"),
                t("Reminder e follow-up automatici", "Automatic reminders and follow-ups"),
                t("Recupero clienti inattivi", "Inactive client recovery"),
              ].map((f, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">{f}</span>
                </div>
              ))}
            </div>

            {!appId ? (
              <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-700 dark:text-amber-400">{t("Configurazione in corso", "Configuration in progress")}</p>
                    <p className="text-xs text-amber-600 dark:text-amber-500 mt-1">
                      {t("L'integrazione WhatsApp Business è in fase di attivazione. Contatta il supporto per accelerare.", "WhatsApp Business integration is being activated. Contact support to speed up.")}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <button
                onClick={startEmbeddedSignup}
                disabled={connecting || !sdkReady}
                className="w-full h-12 rounded-xl font-semibold text-white bg-[#25D366] hover:bg-[#20BD5A] shadow-lg shadow-[#25D366]/20 hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 transition-all disabled:opacity-50 disabled:hover:translate-y-0 flex items-center justify-center gap-2.5"
                data-testid="wa-connect-btn"
              >
                {connecting ? (
                  <><Loader2 className="w-5 h-5 animate-spin" />{t("Connessione in corso...", "Connecting...")}</>
                ) : (
                  <>
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.117.553 4.106 1.519 5.834L.064 23.41l5.753-1.509A11.94 11.94 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.813c-1.94 0-3.742-.557-5.272-1.52l-.378-.224-3.918 1.028 1.046-3.822-.247-.393A9.787 9.787 0 012.187 12c0-5.414 4.399-9.813 9.813-9.813S21.813 6.586 21.813 12s-4.399 9.813-9.813 9.813z"/></svg>
                    {t("Collega WhatsApp Business", "Connect WhatsApp Business")}
                  </>
                )}
              </button>
            )}

            <p className="text-[11px] text-center text-muted-foreground">
              {t("Verrà aperto il flusso ufficiale Meta. Nessuna credenziale richiesta.", "The official Meta flow will open. No credentials required.")}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppConnect;
